import { tv, type VariantProps } from "@ui/lib/tv";

/**
 * Per-option pastel tint for colour-keyed option rows (SearchBar's When duration/month rows,
 * Who member rows). One tint grammar, two selection recipes:
 *
 * - `single`: idle rows whisper their own colour at -25 on hover; the selected row sits at
 *   -50 (matching its icon colour) and deepens to -100 on focus. Hover on a selected row
 *   stays pinned at -50 — the pick is done.
 * - `multiple`: idle rows tint at -50 (the app's pastel tints; green-50 is the exact Figma
 *   #f6f8ea) and the selected row deepens to -100 on hover, inviting a toggle-off.
 *
 * `gray` maps to the neutral ramp. Full literal class strings so the Tailwind v4 scanner
 * emits them — never build these from `${color}` templates.
 */
export const optionTint = tv({
  variants: {
    color: { blue: "", green: "", yellow: "", orange: "", purple: "", red: "", gray: "" },
    isSelected: { true: "", false: "" },
    select: { single: "", multiple: "" },
  },
  compoundVariants: [
    // Single-select (quick-pick lists)
    { select: "single", isSelected: false, color: "blue", class: "hover:bg-blue-25 focus-visible:bg-blue-25" },
    { select: "single", isSelected: false, color: "green", class: "hover:bg-green-25 focus-visible:bg-green-25" },
    { select: "single", isSelected: false, color: "yellow", class: "hover:bg-yellow-25 focus-visible:bg-yellow-25" },
    { select: "single", isSelected: false, color: "orange", class: "hover:bg-orange-25 focus-visible:bg-orange-25" },
    { select: "single", isSelected: false, color: "purple", class: "hover:bg-purple-25 focus-visible:bg-purple-25" },
    { select: "single", isSelected: false, color: "red", class: "hover:bg-red-25 focus-visible:bg-red-25" },
    { select: "single", isSelected: false, color: "gray", class: "hover:bg-neutral-25 focus-visible:bg-neutral-25" },
    {
      select: "single",
      isSelected: true,
      color: "blue",
      class: "bg-blue-50 hover:bg-blue-50 focus-visible:bg-blue-100",
    },
    {
      select: "single",
      isSelected: true,
      color: "green",
      class: "bg-green-50 hover:bg-green-50 focus-visible:bg-green-100",
    },
    {
      select: "single",
      isSelected: true,
      color: "yellow",
      class: "bg-yellow-50 hover:bg-yellow-50 focus-visible:bg-yellow-100",
    },
    {
      select: "single",
      isSelected: true,
      color: "orange",
      class: "bg-orange-50 hover:bg-orange-50 focus-visible:bg-orange-100",
    },
    {
      select: "single",
      isSelected: true,
      color: "purple",
      class: "bg-purple-50 hover:bg-purple-50 focus-visible:bg-purple-100",
    },
    { select: "single", isSelected: true, color: "red", class: "bg-red-50 hover:bg-red-50 focus-visible:bg-red-100" },
    {
      select: "single",
      isSelected: true,
      color: "gray",
      class: "bg-neutral-50 hover:bg-neutral-50 focus-visible:bg-neutral-100",
    },
    // Multi-select (toggle lists)
    { select: "multiple", isSelected: false, color: "blue", class: "hover:bg-blue-50 focus-visible:bg-blue-50" },
    { select: "multiple", isSelected: false, color: "green", class: "hover:bg-green-50 focus-visible:bg-green-50" },
    { select: "multiple", isSelected: false, color: "yellow", class: "hover:bg-yellow-50 focus-visible:bg-yellow-50" },
    { select: "multiple", isSelected: false, color: "orange", class: "hover:bg-orange-50 focus-visible:bg-orange-50" },
    { select: "multiple", isSelected: false, color: "purple", class: "hover:bg-purple-50 focus-visible:bg-purple-50" },
    { select: "multiple", isSelected: false, color: "red", class: "hover:bg-red-50 focus-visible:bg-red-50" },
    { select: "multiple", isSelected: false, color: "gray", class: "hover:bg-neutral-50 focus-visible:bg-neutral-50" },
    { select: "multiple", isSelected: true, color: "blue", class: "bg-blue-50 hover:bg-blue-100" },
    { select: "multiple", isSelected: true, color: "green", class: "bg-green-50 hover:bg-green-100" },
    { select: "multiple", isSelected: true, color: "yellow", class: "bg-yellow-50 hover:bg-yellow-100" },
    { select: "multiple", isSelected: true, color: "orange", class: "bg-orange-50 hover:bg-orange-100" },
    { select: "multiple", isSelected: true, color: "purple", class: "bg-purple-50 hover:bg-purple-100" },
    { select: "multiple", isSelected: true, color: "red", class: "bg-red-50 hover:bg-red-100" },
    { select: "multiple", isSelected: true, color: "gray", class: "bg-neutral-50 hover:bg-neutral-100" },
  ],
  defaultVariants: { isSelected: false, select: "single" },
});

export type OptionTintColor = NonNullable<VariantProps<typeof optionTint>["color"]>;
